const CORE_PNL_FIELDS = ['netPnl', 'net_pnl', 'netProfit', 'net_profit', 'profit'] as const;

const PNL_KEY_RE =
  /(^|[\s_.-])(net[\s_.-]*)?(p\s*&?\s*l|p\/l|pnl|profit|profit\s*\/\s*loss|realized|realised|closed[\s_.-]*pnl|risultato|guadagno)([\s_.-]|$)/i;

const PNL_KEY_BLACKLIST_RE =
  /(factor|ratio|rate|percent|percentage|%|commission|swap|fee|fees|tax|volume|quantity|size|price|entry|exit|balance|equity|drawdown|duration|time|date|symbol|type|direction|winrate|win_rate|lossrate|loss_rate)/i;

export const toFiniteTradeNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined) return 0;

  let raw = String(value).trim();
  if (!raw) return 0;

  const isNegativeParentheses = /^\(.*\)$/.test(raw);
  raw = raw
    .replace(/[()]/g, '')
    .replace(/[€$£%\s]/g, '')
    .replace(/[^0-9,.-]/g, '');

  if (!raw || raw === '-' || raw === ',' || raw === '.') return 0;

  const lastComma = raw.lastIndexOf(',');
  const lastDot = raw.lastIndexOf('.');

  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
    raw = raw.split(thousandsSeparator).join('');
    raw = raw.replace(decimalSeparator, '.');
  } else if (lastComma !== -1) {
    raw = raw.replace(',', '.');
  }

  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) return 0;
  return isNegativeParentheses ? -Math.abs(parsed) : parsed;
};

const isMeaningful = (value: unknown): boolean => toFiniteTradeNumber(value) !== 0;


const isPnlLikeKey = (key: string): boolean => {
  const normalized = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  return PNL_KEY_RE.test(normalized) && !PNL_KEY_BLACKLIST_RE.test(normalized);
};

const collectCandidateObjects = (trade: any): any[] => [
  trade,
  trade?.custom_data,
  trade?.customData,
  trade?.customFields,
  trade?.raw,
  trade?.rawData,
].filter(Boolean);

/**
 * Single P&L resolver for imported trades.
 *
 * Normal case: use netPnl/netProfit/profit.
 * Defensive case: if those fields are zero/missing, recover from raw/custom
 * columns such as "Net P&L", "P/L", "Realized PnL" or "Profit".
 */
export const resolveTradePnl = (trade: any): number => {
  if (!trade) return 0;

  // A zero here is skipped rather than trusted, and that is deliberate.
  //
  // It is tempting to say the first *present* field wins, so that a trade which
  // closed exactly flat reports 0 instead of falling through to a gross figure.
  // It does not work: `net_pnl` arrives as 0 from the database when the column
  // was never populated, so "present zero wins" reads every such trade as
  // break-even. Trying it turned a real account of 23 trades into 23 scratches
  // worth €0.00.
  //
  // The two zeroes — "flat trade" and "never filled in" — cannot be told apart
  // by value, so the ambiguity is resolved where it is created instead: a
  // producer that knows a net is genuinely zero must not also carry a
  // contradictory gross in `profit`. See `mapDemoTrades` in DataFilesContext.
  for (const field of CORE_PNL_FIELDS) {
    if (isMeaningful(trade[field])) return toFiniteTradeNumber(trade[field]);
  }

  for (const source of collectCandidateObjects(trade)) {
    for (const field of CORE_PNL_FIELDS) {
      if (isMeaningful(source[field])) return toFiniteTradeNumber(source[field]);
    }
  }

  for (const source of collectCandidateObjects(trade)) {
    for (const [key, value] of Object.entries(source)) {
      if (isPnlLikeKey(key) && isMeaningful(value)) {
        return toFiniteTradeNumber(value);
      }
    }
  }

  return toFiniteTradeNumber(trade.netPnl ?? trade.netProfit ?? trade.profit);
};
